import { useMemo } from "react";

import find from "lodash/find";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";

import { SystemGroupRoleEnum } from "../api/generated/graphql";
import { userIsAllowed } from "../authorization/authorization.utils";
import { useWaterlyAppStore } from "../stores";

export const useLoggedInUser = () => useWaterlyAppStore((s) => s.loggedInUser);
export const useHasLoggedInUser = () => useLoggedInUser() !== null;
export const useLoggedInUserId = () => useLoggedInUser()?.id;
export const useIsLoggedInUserSuperadmin = () => {
  const loggedInUser = useLoggedInUser();
  if (!loggedInUser) {
    return false;
  }

  return loggedInUser.superadmin || loggedInUser.readonlySuperadmin;
};
export const useLoggedInUserDisplayName = () => useLoggedInUser()?.displayName;
export const useLoggedInUserEnableIphoneMetricNumberInput = () =>
  useLoggedInUser()?.enableIphoneMetricNumberInput ?? false;
export const useLoggedInUserFullNameOrEmail = () => useLoggedInUser()?.fullNameOrEmail;
export const useLoggedInUserPrimaryOrganizationId = () => useLoggedInUser()?.primaryOrganizationId;
export const useLoggedInUserPermissions = () => useLoggedInUser()?.permissions ?? [];
export const useLoggedInUserSystemGroupPermissions = () => {
  const loggedInUserPermissions = useLoggedInUserPermissions();
  return useMemo(
    () => loggedInUserPermissions.flatMap((userPermissions) => userPermissions.systemGroupPermissions),
    [loggedInUserPermissions],
  );
};
export const useLoggedInUserSystemPermissions = () => {
  const loggedInUserPermissions = useLoggedInUserPermissions();
  return useMemo(
    () => loggedInUserPermissions.flatMap((userPermissions) => userPermissions.systemPermissions),
    [loggedInUserPermissions],
  );
};
export const useIsSupervisorOnAnySystem = () => {
  const isLoggedInUserSuperadmin = useIsLoggedInUserSuperadmin();
  const loggedInUserSystemPermissions = useLoggedInUserSystemPermissions();

  return useMemo(
    () =>
      loggedInUserSystemPermissions.some((userPermission) =>
        userIsAllowed(SystemGroupRoleEnum.Supervisor, userPermission.role, isLoggedInUserSuperadmin),
      ),
    [isLoggedInUserSuperadmin, loggedInUserSystemPermissions],
  );
};
export const useIsSupervisorForSystem = (systemId: number) => {
  const isLoggedInUserSuperadmin = useIsLoggedInUserSuperadmin();
  const loggedInUserSystemPermissions = useLoggedInUserSystemPermissions();

  return useMemo(() => {
    const systemPermission = loggedInUserSystemPermissions.find(
      (permission) => permission.systemId === systemId,
    );

    return userIsAllowed(SystemGroupRoleEnum.Supervisor, systemPermission?.role, isLoggedInUserSuperadmin);
  }, [isLoggedInUserSuperadmin, systemId, loggedInUserSystemPermissions]);
};
export const useIsAdminOnAnySystemGroup = () => {
  const isLoggedInUserSuperadmin = useIsLoggedInUserSuperadmin();
  const loggedInUserSystemGroupPermissions = useLoggedInUserSystemGroupPermissions();

  return useMemo(
    () =>
      loggedInUserSystemGroupPermissions.some((userPermission) =>
        userIsAllowed(SystemGroupRoleEnum.Admin, userPermission.role, isLoggedInUserSuperadmin),
      ),
    [isLoggedInUserSuperadmin, loggedInUserSystemGroupPermissions],
  );
};
export const useIsAdminOnSystemGroup = (systemGroupId: number) => {
  const isLoggedInUserSuperadmin = useIsLoggedInUserSuperadmin();
  const loggedInUserSystemGroupPermissions = useLoggedInUserSystemGroupPermissions();

  return useMemo(() => {
    const systemGroupPermission = loggedInUserSystemGroupPermissions.find(
      (permission) => permission.systemGroupId === systemGroupId,
    );

    return userIsAllowed(SystemGroupRoleEnum.Admin, systemGroupPermission?.role, isLoggedInUserSuperadmin);
  }, [isLoggedInUserSuperadmin, loggedInUserSystemGroupPermissions, systemGroupId]);
};

// this leverages all the permissions the user has to find the organizationId for a given systemId, so it should be efficient without needing an additional query
export const useGetOrganizationIdFromSystem = (systemId: number): number | undefined => {
  const loggedInUserPermissions = useLoggedInUserPermissions();

  return useMemo(() => {
    return loggedInUserPermissions?.find((permission) => {
      const { systemPermissions } = permission ?? [];
      return systemPermissions?.find((systemPermission) => {
        const { systemId: systemIdToMatch } = systemPermission ?? "";
        return systemIdToMatch === systemId;
      });
    })?.organizationId;
  }, [systemId, loggedInUserPermissions]);
};
export const useAllAccessibleOrganizations = (): Array<{ id: number; name: string }> => {
  const loggedInUserPermissions = useLoggedInUserPermissions();

  return useMemo(
    () =>
      loggedInUserPermissions.map((permission) => ({
        id: permission.organizationId,
        name: permission.name,
      })),
    [loggedInUserPermissions],
  );
};
export const useAllAccessibleSystemsInOrganization = (
  organizationId: number | null,
): Array<{ id: number; name: string }> => {
  const loggedInUserPermissions = useLoggedInUserPermissions();

  return useMemo(() => {
    if (isNull(organizationId)) {
      return [];
    }

    const permissions = find(loggedInUserPermissions, { organizationId });
    if (isUndefined(permissions)) {
      return [];
    }

    return permissions.systemPermissions.map((systemPermission) => ({
      id: systemPermission.systemId,
      name: systemPermission.name,
    }));
  }, [organizationId, loggedInUserPermissions]);
};
export const useIsOrganizationSupervisor = (organizationId: number) => {
  const isLoggedInUserSuperadmin = useIsLoggedInUserSuperadmin();
  const loggedInUserPermissions = useLoggedInUserPermissions();

  return useMemo(() => {
    // find the permission for the organization
    const organizationPermission = loggedInUserPermissions.find(
      (permission) => permission.organizationId === organizationId,
    );

    return userIsAllowed(
      SystemGroupRoleEnum.Supervisor,
      organizationPermission?.organizationRole,
      isLoggedInUserSuperadmin,
    );
  }, [organizationId, isLoggedInUserSuperadmin, loggedInUserPermissions]);
};
